import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft, Shield, CheckCircle2, XCircle, Clock, Eye, Play,
  ChevronDown, Search, AlertTriangle, FileText, Users
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { LogoFull } from '@/components/Logo';
import { api } from '@/services/api';
import { toast } from 'sonner';

const STATUS_COLORS = {
  pending: '#F59E0B', documents_submitted: '#00AEEF', in_review: '#3D5A80',
  approved: '#83CF44', denied: '#EF4444', pending_configuration: '#8DA0B9'
};

const KycAdminReview = () => {
  const [queue, setQueue] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [denyReason, setDenyReason] = useState('');

  const load = async () => {
    try {
      const [q, s] = await Promise.all([api.getKycQueue(filter || undefined), api.getKycStats()]);
      setQueue(q);
      setStats(s);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [filter]);

  const handleReview = async (id, action) => {
    try {
      const result = await api.reviewKyc(id, action, reviewNotes, denyReason);
      setSelected(result);
      toast.success(`KYC ${action === 'approve' ? 'approved' : action === 'deny' ? 'denied' : action === 'start_review' ? 'review started' : 'updated'}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Action failed'); }
  };

  const filtered = queue.filter(v =>
    !search || v.full_name?.toLowerCase().includes(search.toLowerCase()) || v.email?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) return (
    <div className="min-h-screen bg-[#F0F4F8] flex items-center justify-center">
      <div className="animate-spin w-8 h-8 border-4 border-[#1A3A64] border-t-transparent rounded-full" />
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F0F4F8]" data-testid="kyc-admin-page">
      <header className="bg-white border-b border-[#DCE4ED] sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin" className="flex items-center gap-2 text-[#8DA0B9] hover:text-[#0D1B2A] transition-colors" data-testid="back-to-admin">
              <ArrowLeft size={18} /><span className="text-sm">Admin</span>
            </Link>
            <div className="w-px h-6 bg-[#DCE4ED]" />
            <h1 className="text-lg font-semibold text-[#0D1B2A]" style={{ fontFamily: 'Outfit, sans-serif' }}>KYC/AML Review</h1>
          </div>
          <LogoFull size="small" />
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8 space-y-6">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-testid="kyc-stats">
            {[
              { label: 'Total', value: stats.total, color: '#1A3A64' },
              { label: 'Pending Review', value: stats.pending_review, color: '#F59E0B' },
              { label: 'Approved', value: stats.approved, color: '#83CF44' },
              { label: 'Approval Rate', value: `${stats.approval_rate}%`, color: '#00AEEF' }
            ].map((m, i) => (
              <div key={i} className="bg-white rounded-xl border border-[#DCE4ED] p-4">
                <p className="text-xs text-[#8DA0B9]">{m.label}</p>
                <p className="text-2xl font-bold" style={{ color: m.color }}>{m.value}</p>
              </div>
            ))}
          </div>
        )}

        <div className="grid lg:grid-cols-5 gap-6">
          {/* Queue List */}
          <div className="lg:col-span-2 space-y-4">
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8DA0B9]" />
                <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search..." className="pl-9 h-9" data-testid="kyc-search" />
              </div>
              <div className="relative">
                <select value={filter} onChange={e => setFilter(e.target.value)} className="border border-[#DCE4ED] rounded-lg px-3 py-2 text-sm appearance-none bg-white pr-8" data-testid="kyc-filter">
                  <option value="">All</option>
                  <option value="documents_submitted">Submitted</option>
                  <option value="in_review">In Review</option>
                  <option value="approved">Approved</option>
                  <option value="denied">Denied</option>
                </select>
                <ChevronDown size={14} className="absolute right-2 top-1/2 -translate-y-1/2 text-[#8DA0B9] pointer-events-none" />
              </div>
            </div>

            <div className="space-y-2 max-h-[600px] overflow-y-auto">
              {filtered.length === 0 ? (
                <div className="bg-white rounded-xl border border-[#DCE4ED] p-8 text-center">
                  <Users size={24} className="mx-auto text-[#DCE4ED] mb-2" />
                  <p className="text-sm text-[#8DA0B9]">No verifications found</p>
                </div>
              ) : filtered.map(v => (
                <div key={v.verification_id}
                  onClick={() => setSelected(v)}
                  className={`bg-white rounded-xl border p-4 cursor-pointer transition-all hover:shadow-sm ${selected?.verification_id === v.verification_id ? 'border-[#00AEEF] shadow-sm' : 'border-[#DCE4ED]'}`}
                  data-testid={`kyc-queue-item-${v.verification_id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-medium text-[#0D1B2A]">{v.full_name || 'Unknown'}</p>
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-medium" style={{ backgroundColor: `${STATUS_COLORS[v.status] || '#8DA0B9'}15`, color: STATUS_COLORS[v.status] || '#8DA0B9' }}>
                      {v.status?.replace(/_/g, ' ')}
                    </span>
                  </div>
                  <p className="text-xs text-[#8DA0B9]">{v.email}</p>
                  <p className="text-xs text-[#8DA0B9]">{new Date(v.created_at).toLocaleDateString()}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Detail Panel */}
          <div className="lg:col-span-3">
            {selected ? (
              <motion.div key={selected.verification_id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-white rounded-xl border border-[#DCE4ED] p-6 space-y-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-base font-semibold text-[#0D1B2A]">{selected.full_name}</p>
                    <p className="text-sm text-[#8DA0B9]">{selected.email} &bull; {selected.verification_id}</p>
                  </div>
                  <span className="px-3 py-1 rounded-full text-xs font-medium" style={{ backgroundColor: `${STATUS_COLORS[selected.status]}15`, color: STATUS_COLORS[selected.status] }}>
                    {selected.status?.replace(/_/g, ' ')}
                  </span>
                </div>

                {/* Info Grid */}
                <div className="grid sm:grid-cols-2 gap-4 text-sm">
                  <div className="p-3 bg-[#F0F4F8] rounded-lg">
                    <p className="text-xs text-[#8DA0B9] mb-1">Personal</p>
                    <p className="text-[#0D1B2A]">DOB: {selected.date_of_birth || '-'}</p>
                    <p className="text-[#0D1B2A]">{selected.address?.street}, {selected.address?.city}, {selected.address?.state} {selected.address?.zip_code}</p>
                  </div>
                  <div className="p-3 bg-[#F0F4F8] rounded-lg">
                    <p className="text-xs text-[#8DA0B9] mb-1">Business</p>
                    <p className="text-[#0D1B2A]">{selected.business_info?.business_name || '-'}</p>
                    <p className="text-[#0D1B2A]">EIN: {selected.business_info?.ein || '-'}</p>
                    <p className="text-[#0D1B2A] capitalize">{selected.business_info?.business_type} - {selected.business_info?.state_of_incorporation}</p>
                  </div>
                  <div className="p-3 bg-[#F0F4F8] rounded-lg sm:col-span-2">
                    <p className="text-xs text-[#8DA0B9] mb-1">Document</p>
                    <p className="text-[#0D1B2A] capitalize">{selected.document_type?.replace(/_/g, ' ')} - {selected.document_number}</p>
                    {selected.document_expiry && <p className="text-[#8DA0B9]">Expires: {selected.document_expiry}</p>}
                  </div>
                </div>

                {/* Checks */}
                {selected.checks && typeof selected.checks === 'object' && Object.values(selected.checks).some(c => typeof c === 'object' && c?.status) && (
                  <div>
                    <p className="text-xs text-[#8DA0B9] mb-2 uppercase tracking-wider">Automated Checks</p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {Object.entries(selected.checks).map(([k, v]) => {
                        if (!v || typeof v !== 'object') return null;
                        const color = v.status === 'passed' || v.status === 'clear' ? '#83CF44' : v.status === 'failed' ? '#EF4444' : '#F59E0B';
                        return (
                          <div key={k} className="p-2 rounded-lg border border-[#DCE4ED]">
                            <div className="flex items-center gap-1.5">
                              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: color }} />
                              <span className="text-xs font-medium capitalize" style={{ color }}>{v.status}</span>
                            </div>
                            <p className="text-[10px] text-[#8DA0B9] capitalize mt-0.5">{k.replace(/_/g, ' ')}</p>
                            {v.score && <p className="text-[10px] text-[#3D5A80]">Score: {v.score}%</p>}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {selected.risk_level && (
                  <div className="p-3 bg-[#F0F4F8] rounded-lg">
                    <span className="text-xs text-[#8DA0B9]">Risk Level: </span>
                    <span className={`text-sm font-bold capitalize ${selected.risk_level === 'low' ? 'text-green-600' : selected.risk_level === 'medium' ? 'text-yellow-600' : 'text-red-600'}`}>
                      {selected.risk_level}
                    </span>
                  </div>
                )}

                {/* Actions */}
                {['documents_submitted', 'in_review', 'pending'].includes(selected.status) && (
                  <div className="border-t border-[#DCE4ED] pt-4 space-y-3">
                    <div>
                      <label className="text-xs text-[#8DA0B9] mb-1 block">Reviewer Notes</label>
                      <Input value={reviewNotes} onChange={e => setReviewNotes(e.target.value)} placeholder="Add notes..." data-testid="kyc-review-notes" />
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {selected.status === 'documents_submitted' && (
                        <Button size="sm" onClick={() => handleReview(selected.verification_id, 'start_review')} className="bg-[#3D5A80] hover:bg-[#1A3A64] text-white" data-testid="kyc-start-review-btn">
                          <Play size={14} className="mr-1" /> Start Review
                        </Button>
                      )}
                      {(selected.status === 'in_review' || selected.status === 'documents_submitted') && (
                        <>
                          <Button size="sm" onClick={() => handleReview(selected.verification_id, 'approve')} className="bg-[#83CF44] hover:bg-[#6DB83A] text-white" data-testid="kyc-approve-btn">
                            <CheckCircle2 size={14} className="mr-1" /> Approve
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => {
                            const reason = denyReason || prompt('Denial reason:');
                            if (reason) handleReview(selected.verification_id, 'deny');
                          }} className="border-red-300 text-red-500 hover:bg-red-50" data-testid="kyc-deny-btn">
                            <XCircle size={14} className="mr-1" /> Deny
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => handleReview(selected.verification_id, 'request_more_info')} data-testid="kyc-request-info-btn">
                            <AlertTriangle size={14} className="mr-1" /> Request Info
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                )}

                {/* Timeline */}
                {selected.timeline?.length > 0 && (
                  <div className="border-t border-[#DCE4ED] pt-4">
                    <p className="text-xs text-[#8DA0B9] mb-2 uppercase tracking-wider">Timeline</p>
                    <div className="space-y-2">
                      {selected.timeline.map((t, i) => (
                        <div key={i} className="flex items-start gap-2 text-xs">
                          <div className="w-1.5 h-1.5 rounded-full bg-[#00AEEF] mt-1.5 flex-shrink-0" />
                          <div>
                            <span className="text-[#0D1B2A]">{t.action}</span>
                            <span className="text-[#8DA0B9] ml-2">{new Date(t.at).toLocaleString()}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </motion.div>
            ) : (
              <div className="bg-white rounded-xl border border-[#DCE4ED] p-12 text-center">
                <Eye size={32} className="mx-auto text-[#DCE4ED] mb-3" />
                <p className="text-sm text-[#8DA0B9]">Select a verification to review</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default KycAdminReview;
